import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import { seatKey, flexZonePeople, totalCapacity } from './arenaUtils';

// Excel export
export const exportToExcel = (sectors, removedSeats, flexZones, configName = 'Aréna') => {
  const wb = XLSX.utils.book_new();
  const stats = totalCapacity(sectors, removedSeats, flexZones);

  // 1. Összesítés sheet
  const summary = [
    ['Konfiguráció', configName],
    ['Generálva', new Date().toLocaleString('hu-HU')],
    [],
    ['ÖSSZESÍTÉS'],
    ['Összes szék (eredeti)', stats.totalSeats],
    ['Eltávolított székek', stats.removedSeats],
    ['Elérhető székek', stats.availableSeats],
    ['Flex zóna emberek', stats.flexPeople],
    ['VÉGSŐ KAPACITÁS', stats.grandTotal],
    [],
    ['SZEKTOROK'],
    ['Szektor', 'Sorok', 'Max ülés/sor', 'Összes', 'Eltávolítva', 'Elérhető'],
  ];
  sectors.forEach((s) => {
    const total = s.seats?.length || 0;
    const rem = (removedSeats[s.id] || []).length;
    summary.push([s.name, s.rows, s.maxSeatsPerRow, total, rem, total - rem]);
  });

  if (flexZones.length > 0) {
    summary.push([]);
    summary.push(['FLEX ZÓNÁK']);
    summary.push(['Név', 'Szélesség (m)', 'Magasság (m)', 'Terület (m²)', 'Fő/m²', 'Emberek']);
    flexZones.forEach((z) => {
      summary.push([
        z.name,
        z.widthM,
        z.heightM,
        (z.widthM * z.heightM).toFixed(1),
        z.peoplePerSqm,
        flexZonePeople(z),
      ]);
    });
  }

  const ws1 = XLSX.utils.aoa_to_sheet(summary);
  ws1['!cols'] = [{ wch: 22 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }];
  XLSX.utils.book_append_sheet(wb, ws1, 'Összesítés');

  // 2. Részletes lista (összes szék státusszal)
  const detail = [['Szektor', 'Sor', 'Ülésszám', 'Státusz']];
  sectors.forEach((s) => {
    const removed = new Set(removedSeats[s.id] || []);
    (s.seats || []).forEach((seat) => {
      const k = seatKey(seat.r, seat.s);
      detail.push([s.name, seat.r, seat.s, removed.has(k) ? 'KIVÉVE' : 'AKTÍV']);
    });
  });
  const ws2 = XLSX.utils.aoa_to_sheet(detail);
  ws2['!cols'] = [{ wch: 12 }, { wch: 8 }, { wch: 10 }, { wch: 12 }];
  XLSX.utils.book_append_sheet(wb, ws2, 'Székek részletesen');

  // 3. Csak eltávolított székek
  const removedList = [['Szektor', 'Sor', 'Ülésszám']];
  sectors.forEach((s) => {
    (removedSeats[s.id] || []).forEach((k) => {
      const m = k.match(/R(\d+)-S(\d+)/);
      if (m) removedList.push([s.name, parseInt(m[1]), parseInt(m[2])]);
    });
  });
  const ws3 = XLSX.utils.aoa_to_sheet(removedList);
  ws3['!cols'] = [{ wch: 12 }, { wch: 8 }, { wch: 10 }];
  XLSX.utils.book_append_sheet(wb, ws3, 'Eltávolított székek');

  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([buf], { type: 'application/octet-stream' });
  const filename = `${configName.replace(/[^\w-]+/g, '_')}_${new Date().toISOString().slice(0, 10)}.xlsx`;
  saveAs(blob, filename);
};

// CSV export
export const exportToCSV = (sectors, removedSeats, configName = 'Aréna') => {
  const rows = [['Szektor', 'Sor', 'Ülésszám', 'Státusz']];
  sectors.forEach((s) => {
    const removed = new Set(removedSeats[s.id] || []);
    (s.seats || []).forEach((seat) => {
      const k = seatKey(seat.r, seat.s);
      rows.push([s.name, seat.r, seat.s, removed.has(k) ? 'KIVÉVE' : 'AKTÍV']);
    });
  });
  const csv = '\uFEFF' + rows.map((r) => r.join(';')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const filename = `${configName.replace(/[^\w-]+/g, '_')}_${new Date().toISOString().slice(0, 10)}.csv`;
  saveAs(blob, filename);
};

// PDF export - A3 landscape, a látott canvas 1:1 megjelenítése
export const exportToPDF = async (sectors, removedSeats, flexZones, stages, configName = 'Aréna') => {
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a3' });
  const pageW = pdf.internal.pageSize.getWidth();   // ~1191
  const pageH = pdf.internal.pageSize.getHeight();  // ~842

  // Footer height (pici cimsor + dátum a PDF alján)
  const footerH = 22;

  const svgEl = document.querySelector('[data-testid="arena-svg"]');
  if (svgEl) {
    const dataUrl = await svgToDataUrl(svgEl, 2.5);
    if (dataUrl) {
      const vb = svgEl.viewBox.baseVal;
      const ratio = vb.width / vb.height;
      const availW = pageW;
      const availH = pageH - footerH;
      let drawW = availW;
      let drawH = drawW / ratio;
      if (drawH > availH) {
        drawH = availH;
        drawW = drawH * ratio;
      }
      const offX = (pageW - drawW) / 2;
      const offY = (availH - drawH) / 2;
      pdf.addImage(dataUrl, 'PNG', offX, offY, drawW, drawH, undefined, 'FAST');
    }
  }

  // Footer
  pdf.setFontSize(9);
  pdf.setTextColor(120);
  const stats = totalCapacity(sectors, removedSeats, flexZones);
  const summary = `${configName}  •  Vegso kapacitas: ${stats.grandTotal.toLocaleString('hu-HU')} fo  •  Elerheto szek: ${stats.availableSeats.toLocaleString('hu-HU')} / ${stats.totalSeats.toLocaleString('hu-HU')}  •  Flex: ${stats.flexPeople.toLocaleString('hu-HU')} fo`;
  pdf.text(summary, 24, pageH - 8);
  pdf.text(new Date().toLocaleString('hu-HU'), pageW - 24, pageH - 8, { align: 'right' });

  pdf.save(`${configName.replace(/[^\w-]+/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`);
};

// SVG -> data URL (rasterizalas canvas-en at)
async function svgToDataUrl(svgEl, scale = 1.5) {
  const clone = svgEl.cloneNode(true);
  const vb = svgEl.viewBox.baseVal;
  // Reset viewBox to full canvas (avoid current zoom/pan state)
  clone.setAttribute('viewBox', `0 0 ${vb.width} ${vb.height}`);
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

  // Inline background image (and any other <image>) as base64 data URI so it
  // renders when the SVG is rasterized via <img>+canvas.
  const images = clone.querySelectorAll('image');
  await Promise.all(Array.from(images).map(async (im) => {
    const href = im.getAttribute('href') || im.getAttribute('xlink:href');
    if (!href || href.startsWith('data:')) return;
    try {
      const res = await fetch(href);
      const blob = await res.blob();
      const dataUri = await new Promise((resolve) => {
        const fr = new FileReader();
        fr.onload = () => resolve(fr.result);
        fr.onerror = () => resolve(null);
        fr.readAsDataURL(blob);
      });
      if (dataUri) {
        im.setAttribute('href', dataUri);
        im.removeAttribute('xlink:href');
      }
    } catch (e) {
      // ignore - image stays as external ref
    }
  }));

  const xml = new XMLSerializer().serializeToString(clone);
  const b64 = btoa(unescape(encodeURIComponent(xml)));
  const dataUri = `data:image/svg+xml;base64,${b64}`;
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = vb.width * scale;
      canvas.height = vb.height * scale;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      try {
        resolve(canvas.toDataURL('image/png'));
      } catch (e) {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = dataUri;
  });
}

